/**
 * RunTab — default portal landing tab (Phase 3.2).
 * Shows progress ring, next-up card, phase accordions with step completion.
 */

import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Button,
  CircularProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import BlockIcon from '@mui/icons-material/Block';
import { Plan, PlanStep, StepStatus } from '../../types/generated/graphql';
import { colors } from '../../theme/colors';

interface RunTabProps {
  plan: Plan;
  onStepClick: (stepId: string) => void;
  onToggleCompletion: (stepId: string, completed: boolean) => void;
}

function statusIcon(status: StepStatus) {
  switch (status) {
    case StepStatus.Completed: return <CheckCircleIcon sx={{ fontSize: 18, color: colors.green[600] }} />;
    case StepStatus.InProgress: return <HourglassBottomIcon sx={{ fontSize: 18, color: colors.blue[600] }} />;
    case StepStatus.WaitingExternal: return <HourglassBottomIcon sx={{ fontSize: 18, color: colors.amber[600] }} />;
    case StepStatus.Blocked: return <BlockIcon sx={{ fontSize: 18, color: colors.red[600] }} />;
    default: return <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: colors.gray[400] }} />;
  }
}

export const RunTab: React.FC<RunTabProps> = ({ plan, onStepClick, onToggleCompletion }) => {
  const steps = plan.steps;
  const total = steps.length;
  const completed = steps.filter((s) => s.status === StepStatus.Completed).length;
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;

  // Find next actionable step (not completed, not blocked)
  const nextUp = useMemo(() => {
    return steps.find(
      (s) => s.status !== StepStatus.Completed && s.status !== StepStatus.Blocked && s.status !== StepStatus.Skipped,
    ) ?? null;
  }, [steps]);

  // Group steps by phase
  const sortedPhases = useMemo(() => [...plan.phases].sort((a, b) => a.order - b.order), [plan.phases]);
  const stepsByPhase = useMemo(() => {
    const map = new Map<string, PlanStep[]>();
    for (const p of sortedPhases) map.set(p.id, []);
    for (const s of steps) {
      const arr = map.get(s.phaseId ?? '');
      if (arr) arr.push(s);
    }
    return map;
  }, [steps, sortedPhases]);

  return (
    <Box>
      {/* Progress ring + headline */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, border: 1, borderColor: 'divider', borderRadius: 2 }}>
        <Stack direction="row" spacing={3} alignItems="center">
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <CircularProgress variant="determinate" value={pct} size={72} thickness={5} />
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{pct}%</Typography>
            </Box>
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {completed} of {total} steps complete
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {total - completed} remaining
            </Typography>
          </Box>
        </Stack>
      </Paper>

      {/* Next-up card */}
      {nextUp && (
        <Paper elevation={0} sx={{ p: 3, mb: 3, border: 1, borderColor: colors.blue[200], borderRadius: 2, bgcolor: colors.blue[50] }}>
          <Typography variant="overline" color="text.secondary">Next up</Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            {nextUp.title}
          </Typography>
          {nextUp.assignedRoleId && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Owner: {nextUp.assignedRoleId}
            </Typography>
          )}
          <Button
            variant="contained"
            size="small"
            startIcon={<PlayArrowIcon />}
            onClick={() => onStepClick(nextUp.id)}
          >
            Start this step
          </Button>
        </Paper>
      )}

      {/* Phase accordions */}
      {sortedPhases.map((phase) => {
        const phaseSteps = stepsByPhase.get(phase.id) ?? [];
        const pDone = phaseSteps.filter((s) => s.status === StepStatus.Completed).length;
        return (
          <Accordion
            key={phase.id}
            disableGutters
            defaultExpanded={false}
            sx={{ mb: 1, '&:before': { display: 'none' }, boxShadow: 1 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {phase.title}
                </Typography>
                <Chip label={`${pDone}/${phaseSteps.length} complete`} size="small" sx={{ bgcolor: 'action.hover' }} />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={0.5}>
                {phaseSteps.map((step) => (
                  <Box
                    key={step.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      py: 1,
                      px: 2,
                      borderRadius: 1,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                    onClick={() => onStepClick(step.id)}
                  >
                    {statusIcon(step.status)}
                    <Typography variant="body2" sx={{ flex: 1 }}>{step.title}</Typography>
                  </Box>
                ))}
              </Stack>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

export default RunTab;
